import React, { useState, useMemo } from 'react';
import { Zap, CheckSquare, Square, CalendarCheck, ClipboardList, Home, Truck, PackageCheck, ArrowRight } from "lucide-react";

const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];
const ITEM_COLORS = {
  "LPG 20kg":   { bg: "#e8f0fb", border: "#185FA5", text: "#185FA5" },
  "LPG 50kg":   { bg: "#ede9fe", border: "#7C3AED", text: "#7C3AED" },
  "산소(O2)":   { bg: "#e8faf2", border: "#059669", text: "#059669" },
  "질소(N2)":   { bg: "#fff7ed", border: "#D97706", text: "#D97706" },
  "아르곤(Ar)": { bg: "#fef2f2", border: "#DC2626", text: "#DC2626" },
  "탄산(CO2)":  { bg: "#f0fdf4", border: "#16A34A", text: "#16A34A" },
};

const ChargingPanel = ({ cylinders, setCylinders, items = [], setCollections, history = [], setHistory, extHistory = [], setExtHistory }) => {
  const gasItems = useMemo(() => {
    const fromSettings = items.length > 0 ? items.map(i => i.name) : DEFAULT_ITEMS;
    const fromCylinders = cylinders.map(c => c.item).filter(Boolean);
    return [...new Set([...fromSettings, ...fromCylinders])];
  }, [items, cylinders]);
  // ── 탭 ──────────────────────────────────────────────────────
  const [mainTab, setMainTab] = useState('internal'); // 'internal' | 'external'

  // ── 내부 충전 상태 ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterItem,  setFilterItem]  = useState("전체");
  const [filterWH,    setFilterWH]    = useState("전체");
  const [chargeDate,  setChargeDate]  = useState(new Date().toISOString().split('T')[0]);
  const [done,        setDone]        = useState(false);

  // ── 외부 충전 상태 ────────────────────────────────────────
  const [extTab,        setExtTab]        = useState('out');   // 'out' | 'in'
  const [extCharger,    setExtCharger]    = useState('');
  const [extDate,       setExtDate]       = useState(new Date().toISOString().split('T')[0]);
  const [extFilterItem, setExtFilterItem] = useState('전체');
  const [extSelectedOut, setExtSelectedOut] = useState([]); // 공병 출고 선택
  const [extSelectedIn,  setExtSelectedIn]  = useState([]); // 실병 입고 선택
  const [extInFilterItem, setExtInFilterItem] = useState('전체'); // 실병입고 품목 필터
  const [extDone,       setExtDone]       = useState('');
  const [inWarehouse,   setInWarehouse]   = useState('제1 창고(메인)');
  const [extInCost,     setExtInCost]     = useState('');

  // ── 공통 계산 ─────────────────────────────────────────────
  const warehouses = useMemo(() => {
    const whs = [...new Set(cylinders.filter(c => c.locType === "warehouse").map(c => c.location))];
    return ["전체", ...whs];
  }, [cylinders]);

  const warehouseList = useMemo(() => {
    return [...new Set(cylinders.filter(c => c.locType === "warehouse").map(c => c.location))];
  }, [cylinders]);

  const emptyCountByItem = useMemo(() =>
    gasItems.reduce((acc, item) => {
      acc[item] = cylinders.filter(c => c.locType === "warehouse" && c.status === "공병" && c.item === item).length;
      return acc;
    }, {}),
  [cylinders, gasItems]);

  const totalEmpty    = cylinders.filter(c => c.locType === "warehouse" && c.status === "공병").length;
  const totalFull     = cylinders.filter(c => c.locType === "warehouse" && c.status === "실병").length;
  const totalExternal = cylinders.filter(c => c.locType === "external").length;

  // 내부 충전용 공병 필터
  const emptyCylinders = useMemo(() =>
    cylinders.filter(c =>
      c.locType === "warehouse" && c.status === "공병" &&
      (filterItem === "전체" || c.item === filterItem) &&
      (filterWH   === "전체" || c.location === filterWH)
    ),
  [cylinders, filterItem, filterWH]);

  // 외부 출고용 공병 (창고)
  const extOutCandidates = useMemo(() =>
    cylinders.filter(c =>
      c.locType === "warehouse" && c.status === "공병" &&
      (extFilterItem === "전체" || c.item === extFilterItem)
    ),
  [cylinders, extFilterItem]);

  // 외부 입고용 실병 대기 (외부 충전소에 있는 것)
  const extInCandidates = useMemo(() =>
    cylinders.filter(c => c.locType === "external"),
  [cylinders]);

  // 실병 입고 품목별 카운트
  const extInItemCounts = useMemo(() =>
    extInCandidates.reduce((acc, c) => {
      if (c.item) acc[c.item] = (acc[c.item] || 0) + 1;
      return acc;
    }, {}),
  [extInCandidates]);

  // 실병 입고 필터 적용
  const filteredExtInCandidates = useMemo(() =>
    extInFilterItem === '전체'
      ? extInCandidates
      : extInCandidates.filter(c => c.item === extInFilterItem),
  [extInCandidates, extInFilterItem]);

  // ── 내부 충전 로직 ────────────────────────────────────────
  const allSelected    = emptyCylinders.length > 0 && emptyCylinders.every(c => selectedIds.includes(c.id));
  const selectedInView = selectedIds.filter(id => emptyCylinders.find(c => c.id === id)).length;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(prev => prev.filter(id => !emptyCylinders.find(c => c.id === id)));
    else setSelectedIds(prev => [...new Set([...prev, ...emptyCylinders.map(c => c.id)])]);
  };
  const toggleOne = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleItemSelect = (item) => { setFilterItem(item); setSelectedIds([]); };

  const handleCharge = () => {
    if (selectedIds.length === 0) return;
    const targets = cylinders.filter(c => selectedIds.includes(c.id));
    const summary = targets.reduce((acc, c) => { acc[c.item] = (acc[c.item] || 0) + 1; return acc; }, {});
    setCylinders(prev => prev.map(c =>
      selectedIds.includes(c.id) ? { ...c, status: "실병", filledAt: chargeDate } : c
    ));
    setHistory(prev => [{ date: chargeDate, count: targets.length, summary, ids: targets.map(c => c.id), type: '내부' }, ...prev]);
    setSelectedIds([]);
    setDone(true);
    setTimeout(() => setDone(false), 2500);
  };

  // ── 외부 충전 로직 ─────────────────────────────────────────

  // 공병 외부 출고
  const handleExtOut = () => {
    if (extSelectedOut.length === 0 || !extCharger.trim()) return;
    const targets = cylinders.filter(c => extSelectedOut.includes(c.id));
    const summary = targets.reduce((acc, c) => { acc[c.item] = (acc[c.item] || 0) + 1; return acc; }, {});
    setCylinders(prev => prev.map(c =>
      extSelectedOut.includes(c.id)
        ? { ...c, locType: 'external', location: extCharger.trim(), sentAt: extDate }
        : c
    ));
    setExtHistory(prev => [{
      type: 'out', date: extDate, charger: extCharger.trim(),
      count: targets.length, summary, ids: [...extSelectedOut]
    }, ...prev]);
    setExtSelectedOut([]);
    setExtDone('출고');
    setTimeout(() => setExtDone(''), 2500);
  };

  // 실병 외부 입고
  const handleExtIn = () => {
    if (extSelectedIn.length === 0) return;
    const targets = cylinders.filter(c => extSelectedIn.includes(c.id));
    const chargerName = targets[0]?.location || '외부충전소';
    const summary = targets.reduce((acc, c) => { acc[c.item] = (acc[c.item] || 0) + 1; return acc; }, {});
    const cost = parseInt(extInCost) || 0;
    setCylinders(prev => prev.map(c =>
      extSelectedIn.includes(c.id)
        ? { ...c, locType: 'warehouse', location: inWarehouse, status: '실병', filledAt: extDate }
        : c
    ));
    setExtHistory(prev => [{
      type: 'in', date: extDate, charger: chargerName,
      count: targets.length, summary, ids: [...extSelectedIn], warehouse: inWarehouse,
      cost,
    }, ...prev]);
    if (cost > 0 && setCollections) {
      setCollections(prev => [...prev, {
        id: Date.now(),
        type: 'service',
        customer: chargerName,
        item: '외부충전 서비스',
        items: { '외부충전 서비스': targets.length },
        quantity: targets.length,
        price: Math.round(cost / targets.length),
        amount: cost,
        date: extDate,
        targetWarehouse: inWarehouse,
        cylinders: [],
      }]);
    }
    setExtSelectedIn([]);
    setExtInCost('');
    setExtDone('입고');
    setTimeout(() => setExtDone(''), 2500);
  };

  const today = new Date().toISOString().split('T')[0];
  const todayCount = history.filter(h => h.date === today).reduce((s, h) => s + h.count, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 상단 요약 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
        <SummaryCard label="창고 내 공병"       value={`${totalEmpty}개`}    color="#D97706" />
        <SummaryCard label="창고 내 실병"       value={`${totalFull}개`}     color="#185FA5" />
        <SummaryCard label="외부 충전 중"       value={`${totalExternal}개`} color="#7C3AED" />
        <SummaryCard label="금일 내부 충전"     value={`${todayCount}개`}    color="#059669" />
      </div>

      {/* 메인 탭 */}
      <div style={{ display: 'flex', background: '#f0f0f0', padding: 4, borderRadius: 12, gap: 4 }}>
        <TabBtn active={mainTab === 'internal'} onClick={() => setMainTab('internal')}
          icon={<Zap size={15} />} label="내부 충전" />
        <TabBtn active={mainTab === 'external'} onClick={() => setMainTab('external')}
          icon={<Truck size={15} />} label="외부 충전소 위탁" badge={totalExternal > 0 ? totalExternal : null} />
      </div>

      {/* ══════════════ 내부 충전 ══════════════ */}
      {mainTab === 'internal' && (
        <div style={sectionBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <Zap size={20} color="#D97706" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>공병 → 실병 충전 처리</h3>
          </div>

          {/* STEP 1 */}
          <div style={{ marginBottom: 20 }}>
            <p style={stepLabel}>STEP 1 · 충전할 품목 선택</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => handleItemSelect("전체")}
                style={itemCardStyle("전체", filterItem, { bg: "#f8f7f2", border: "#73726c", text: "#73726c" })}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>전체</span>
                <span style={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>{totalEmpty}</span>
                <span style={{ fontSize: 11, opacity: 0.8 }}>공병</span>
              </button>
              {gasItems.map(item => {
                const color = ITEM_COLORS[item] || { bg: '#f0f4f8', border: '#73726c', text: '#73726c' };
                return (
                  <button key={item} onClick={() => handleItemSelect(item)}
                    style={itemCardStyle(item, filterItem, color)}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{item}</span>
                    <span style={{ fontSize: 22, fontWeight: 900, lineHeight: 1 }}>{emptyCountByItem[item]}</span>
                    <span style={{ fontSize: 11, opacity: 0.8 }}>공병</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* STEP 2 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <p style={{ ...stepLabel, margin: 0 }}>
                STEP 2 · 충전할 용기 선택
                <span style={{ fontWeight: 400, color: '#999', marginLeft: 8 }}>
                  ({filterItem === "전체" ? "전체 품목" : filterItem} · 공병 {emptyCylinders.length}개)
                </span>
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Home size={13} color="#888" />
                <select value={filterWH} onChange={e => setFilterWH(e.target.value)} style={selectStyle}>
                  {warehouses.map(w => <option key={w}>{w}</option>)}
                </select>
              </div>
            </div>
            <CylinderChecklist
              cylinders={emptyCylinders}
              selectedIds={selectedIds}
              allSelected={allSelected}
              selectedInView={selectedInView}
              toggleAll={toggleAll}
              toggleOne={toggleOne}
              showItem={filterItem === "전체"}
            />
          </div>

          {/* STEP 3 */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 18 }}>
            <p style={stepLabel}>STEP 3 · 충전일 확인 후 처리</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CalendarCheck size={15} color="#555" />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>충전일</span>
                <input type="date" value={chargeDate} onChange={e => setChargeDate(e.target.value)} style={selectStyle} />
              </div>
              <button onClick={handleCharge} disabled={selectedIds.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 28px',
                  background: selectedIds.length === 0 ? '#e5e3db' : '#059669',
                  color: selectedIds.length === 0 ? '#aaa' : '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                  cursor: selectedIds.length === 0 ? 'default' : 'pointer' }}>
                <Zap size={16} /> 충전 처리 ({selectedIds.length}개)
              </button>
              {done && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>✓ 충전 완료!</span>}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ 외부 충전 ══════════════ */}
      {mainTab === 'external' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* 외부 서브탭 */}
          <div style={{ display: 'flex', gap: 0, background: 'white', border: '1px solid #d3d1c7', borderRadius: 12, overflow: 'hidden' }}>
            <button onClick={() => setExtTab('out')}
              style={{ flex: 1, padding: '14px 0', border: 'none', background: extTab === 'out' ? '#fff7ed' : 'white',
                color: extTab === 'out' ? '#D97706' : '#888', fontWeight: extTab === 'out' ? 700 : 400,
                fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                borderBottom: extTab === 'out' ? '3px solid #D97706' : '3px solid transparent' }}>
              <Truck size={16} /> 공병 외부 출고
            </button>
            <button onClick={() => setExtTab('in')}
              style={{ flex: 1, padding: '14px 0', border: 'none', background: extTab === 'in' ? '#f0fdf8' : 'white',
                color: extTab === 'in' ? '#059669' : '#888', fontWeight: extTab === 'in' ? 700 : 400,
                fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                borderBottom: extTab === 'in' ? '3px solid #059669' : '3px solid transparent' }}>
              <PackageCheck size={16} /> 실병 입고 (충전 완료)
              {extInCandidates.length > 0 && (
                <span style={{ background: '#059669', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>
                  {extInCandidates.length}
                </span>
              )}
            </button>
          </div>

          {/* ── 공병 외부 출고 ── */}
          {extTab === 'out' && (
            <div style={sectionBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <Truck size={20} color="#D97706" />
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>공병 → 외부 충전소 출고</h3>
              </div>

              {/* 충전소명 + 날짜 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={miniLabel}>외부 충전소명</label>
                  <input
                    style={inputStyle}
                    placeholder="예: 한국가스공사 춘천충전소"
                    value={extCharger}
                    onChange={e => setExtCharger(e.target.value)}
                  />
                </div>
                <div>
                  <label style={miniLabel}>출고일</label>
                  <input type="date" style={inputStyle} value={extDate} onChange={e => setExtDate(e.target.value)} />
                </div>
              </div>

              {/* 품목 필터 */}
              <p style={stepLabel}>충전 보낼 공병 선택</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {["전체", ...gasItems].map(item => (
                  <button key={item} onClick={() => setExtFilterItem(item)}
                    style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: extFilterItem === item ? '#D97706' : '#f0f0f0',
                      color: extFilterItem === item ? '#fff' : '#666' }}>
                    {item}
                  </button>
                ))}
              </div>

              <CylinderChecklist
                cylinders={extOutCandidates}
                selectedIds={extSelectedOut}
                allSelected={extOutCandidates.length > 0 && extOutCandidates.every(c => extSelectedOut.includes(c.id))}
                selectedInView={extSelectedOut.filter(id => extOutCandidates.find(c => c.id === id)).length}
                toggleAll={() => {
                  const all = extOutCandidates.every(c => extSelectedOut.includes(c.id));
                  if (all) setExtSelectedOut(prev => prev.filter(id => !extOutCandidates.find(c => c.id === id)));
                  else setExtSelectedOut(prev => [...new Set([...prev, ...extOutCandidates.map(c => c.id)])]);
                }}
                toggleOne={(id) => setExtSelectedOut(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                showItem={extFilterItem === "전체"}
                emptyText="창고에 공병이 없습니다"
              />

              <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <button onClick={handleExtOut}
                  disabled={extSelectedOut.length === 0 || !extCharger.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 28px',
                    background: (extSelectedOut.length === 0 || !extCharger.trim()) ? '#e5e3db' : '#D97706',
                    color: (extSelectedOut.length === 0 || !extCharger.trim()) ? '#aaa' : '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                    cursor: (extSelectedOut.length === 0 || !extCharger.trim()) ? 'default' : 'pointer' }}>
                  <ArrowRight size={16} /> 외부 충전소로 출고 ({extSelectedOut.length}개)
                </button>
                {extDone === '출고' && <span style={{ fontSize: 13, color: '#D97706', fontWeight: 700 }}>✓ 출고 처리 완료!</span>}
              </div>

              {extCharger.trim() === '' && extSelectedOut.length > 0 && (
                <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#DC2626', fontWeight: 600 }}>충전소명을 입력하세요.</p>
              )}
            </div>
          )}

          {/* ── 실병 입고 ── */}
          {extTab === 'in' && (
            <div style={sectionBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <PackageCheck size={20} color="#059669" />
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>외부 충전 완료 → 실병 입고</h3>
              </div>

              {extInCandidates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb', fontSize: 14, border: '1px dashed #e5e3db', borderRadius: 10 }}>
                  외부 충전소에 맡긴 용기가 없습니다.<br />
                  <span style={{ fontSize: 12 }}>먼저 "공병 외부 출고"를 진행하세요.</span>
                </div>
              ) : (
                <>
                  {/* 품목 필터 버튼 */}
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ ...stepLabel, marginBottom: 8 }}>품목 필터</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => setExtInFilterItem('전체')}
                        style={{ ...inFilterBtnStyle, background: extInFilterItem === '전체' ? '#059669' : '#f0f0f0', color: extInFilterItem === '전체' ? '#fff' : '#666' }}>
                        전체 <span style={{ fontWeight: 800 }}>{extInCandidates.length}</span>개
                      </button>
                      {Object.entries(extInItemCounts).map(([item, cnt]) => {
                        const col = ITEM_COLORS[item] || { bg: '#e8f0fb', border: '#185FA5', text: '#185FA5' };
                        const active = extInFilterItem === item;
                        return (
                          <button key={item} onClick={() => setExtInFilterItem(item)}
                            style={{ ...inFilterBtnStyle, background: active ? col.border : col.bg, color: active ? '#fff' : col.text, border: `1.5px solid ${active ? col.border : 'transparent'}` }}>
                            {item} <span style={{ fontWeight: 800 }}>{cnt}</span>개
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* 충전소별 그룹 (필터 적용) */}
                  {filteredExtInCandidates.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10, marginBottom: 16 }}>
                      선택한 품목의 입고 대기 용기가 없습니다
                    </div>
                  ) : (
                    [...new Set(filteredExtInCandidates.map(c => c.location))].map(charger => {
                      const group = filteredExtInCandidates.filter(c => c.location === charger);
                      return (
                        <div key={charger} style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Truck size={13} /> {charger} ({group.length}개)
                          </div>
                          <div style={{ border: '1px solid #e5e3db', borderRadius: 10, overflow: 'hidden' }}>
                            {/* 전체선택 행 */}
                            <div onClick={() => {
                              const groupIds = group.map(c => c.id);
                              const allChecked = groupIds.every(id => extSelectedIn.includes(id));
                              if (allChecked) setExtSelectedIn(prev => prev.filter(id => !groupIds.includes(id)));
                              else setExtSelectedIn(prev => [...new Set([...prev, ...groupIds])]);
                            }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: '#f8f7f2', borderBottom: '1px solid #e5e3db', cursor: 'pointer', userSelect: 'none' }}>
                              {group.every(c => extSelectedIn.includes(c.id))
                                ? <CheckSquare size={16} color="#059669" />
                                : <Square size={16} color="#aaa" />}
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>전체 선택 ({group.length}개)</span>
                              {group.filter(c => extSelectedIn.includes(c.id)).length > 0 && (
                                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#059669', fontWeight: 700 }}>
                                  {group.filter(c => extSelectedIn.includes(c.id)).length}개 선택됨
                                </span>
                              )}
                            </div>
                            {group.map(c => {
                              const checked = extSelectedIn.includes(c.id);
                              const col = ITEM_COLORS[c.item];
                              return (
                                <div key={c.id} onClick={() => setExtSelectedIn(prev => checked ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: checked ? '#f0fdf8' : '#fff', userSelect: 'none' }}>
                                  {checked
                                    ? <CheckSquare size={17} color="#059669" />
                                    : <Square size={17} color="#ccc" />}
                                  <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, flex: 1, color: '#333' }}>{c.id}</span>
                                  {c.item && <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: col ? col.bg : '#e8f0fb', color: col ? col.text : '#185FA5' }}>{c.item}</span>}
                                  {c.sentAt && <span style={{ fontSize: 11, color: '#aaa' }}>출고일: {c.sentAt}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* 입고 창고 + 날짜 + 충전 비용 */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
                    <div>
                      <label style={miniLabel}>입고 창고</label>
                      <select style={inputStyle} value={inWarehouse} onChange={e => setInWarehouse(e.target.value)}>
                        {(warehouseList.length > 0 ? warehouseList : ['제1 창고(메인)', '야외 적재소']).map(w => (
                          <option key={w}>{w}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={miniLabel}>입고일</label>
                      <input type="date" style={inputStyle} value={extDate} onChange={e => setExtDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={miniLabel}>충전 비용 총액 (원)</label>
                      <input type="number" style={inputStyle} placeholder="0 (없으면 생략)"
                        value={extInCost} onChange={e => setExtInCost(e.target.value)} min={0} />
                    </div>
                  </div>
                  {parseInt(extInCost) > 0 && (
                    <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#7C3AED', fontWeight: 600 }}>
                      ✓ 충전 비용 {parseInt(extInCost).toLocaleString()}원이 매입 내역에 자동 등록됩니다.
                    </p>
                  )}

                  <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button onClick={handleExtIn}
                      disabled={extSelectedIn.length === 0}
                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 28px',
                        background: extSelectedIn.length === 0 ? '#e5e3db' : '#059669',
                        color: extSelectedIn.length === 0 ? '#aaa' : '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14,
                        cursor: extSelectedIn.length === 0 ? 'default' : 'pointer' }}>
                      <PackageCheck size={16} /> 실병 입고 처리 ({extSelectedIn.length}개)
                    </button>
                    {extDone === '입고' && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>✓ 입고 처리 완료!</span>}
                  </div>
                </>
              )}
            </div>
          )}

          {/* 외부 충전 이력 */}
          {extHistory.length > 0 && (
            <div style={sectionBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <ClipboardList size={18} color="#7C3AED" />
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>외부 충전 이력</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {extHistory.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e3db',
                    background: h.type === 'out' ? '#fff7ed' : '#f0fdf8', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                      background: h.type === 'out' ? '#D97706' : '#059669', color: '#fff' }}>
                      {h.type === 'out' ? '출고' : '입고'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#333', minWidth: 90 }}>{h.date}</span>
                    <span style={{ fontSize: 13, color: '#7C3AED', fontWeight: 700 }}>{h.charger}</span>
                    <span style={{ fontSize: 13, color: h.type === 'out' ? '#D97706' : '#059669', fontWeight: 700 }}>
                      총 {h.count}개 {h.type === 'out' ? '출고' : '실병 입고'}
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                      {Object.entries(h.summary).map(([item, cnt]) => {
                        const col = ITEM_COLORS[item] || { bg: '#e8f0fb', text: '#185FA5' };
                        return (
                          <span key={item} style={{ fontSize: 12, background: col.bg, color: col.text, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                            {item} {cnt}개
                          </span>
                        );
                      })}
                    </div>
                    {h.warehouse && (
                      <span style={{ fontSize: 12, color: '#888' }}>→ {h.warehouse}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 내부 충전 이력 */}
      {mainTab === 'internal' && (
        <div style={sectionBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <ClipboardList size={18} color="#185FA5" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>충전 이력</h3>
          </div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>충전 이력이 없습니다</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {history.map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#f8f7f2', borderRadius: 10, border: '1px solid #e5e3db', flexWrap: 'wrap' }}>
                  <CalendarCheck size={14} color="#185FA5" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#333', minWidth: 90 }}>{h.date}</span>
                  <span style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>총 {h.count}개 충전</span>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                    {Object.entries(h.summary).map(([item, cnt]) => {
                      const col = ITEM_COLORS[item] || { bg: '#e8f0fb', text: '#185FA5' };
                      return (
                        <span key={item} style={{ fontSize: 12, background: col.bg, color: col.text, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                          {item} {cnt}개
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── 공통 컴포넌트 ─────────────────────────────────────────────

const CylinderChecklist = ({ cylinders, selectedIds, allSelected, selectedInView, toggleAll, toggleOne, showItem, emptyText = "공병이 없습니다" }) => {
  if (cylinders.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0', color: '#bbb', fontSize: 14, border: '1px dashed #ddd', borderRadius: 10 }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid #e5e3db', borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={toggleAll}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#f8f7f2', borderBottom: '1px solid #e5e3db', cursor: 'pointer', userSelect: 'none' }}>
        {allSelected ? <CheckSquare size={18} color="#185FA5" /> : <Square size={18} color="#aaa" />}
        <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>전체 선택 ({cylinders.length}개)</span>
        {selectedInView > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#185FA5', fontWeight: 700 }}>{selectedInView}개 선택됨</span>
        )}
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {cylinders.map(c => {
          const checked = selectedIds.includes(c.id);
          const col = ITEM_COLORS[c.item];
          return (
            <div key={c.id} onClick={() => toggleOne(c.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: checked ? '#f0f6ff' : '#fff', userSelect: 'none' }}>
              {checked ? <CheckSquare size={17} color="#185FA5" /> : <Square size={17} color="#ccc" />}
              <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, flex: 1, color: '#333' }}>{c.id}</span>
              {showItem && c.item && (
                <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: col ? col.bg : '#e8f0fb', color: col ? col.text : '#185FA5' }}>{c.item}</span>
              )}
              <span style={{ fontSize: 12, color: '#888', background: '#f5f5f5', padding: '2px 8px', borderRadius: 10 }}>{c.location}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, icon, label, badge }) => (
  <button onClick={onClick} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    padding: '10px 0', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: active ? 700 : 400,
    background: active ? 'white' : 'transparent', color: active ? '#333' : '#888', cursor: 'pointer',
    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
  {icon} {label}
  {badge && <span style={{ background: '#7C3AED', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{badge}</span>}
</button>
);

const SummaryCard = ({ label, value, color }) => (
  <div style={{ background: 'white', padding: '18px 20px', borderRadius: 14, border: '1px solid #d3d1c7' }}>
    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
  </div>
);

const inFilterBtnStyle = { padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.15s' };
const sectionBox  = { background: 'white', padding: 24, borderRadius: 16, border: '1px solid #d3d1c7' };
const selectStyle = { padding: '7px 10px', border: '1px solid #d3d1c7', borderRadius: 8, fontSize: 13, background: '#fcfbf7', cursor: 'pointer' };
const inputStyle  = { width: '100%', padding: '10px 12px', border: '1px solid #d3d1c7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fcfbf7' };
const miniLabel   = { display: 'block', fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 6 };
const stepLabel   = { margin: '0 0 10px 0', fontSize: 13, fontWeight: 700, color: '#555' };
const itemCardStyle = (item, selected, color) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 2, padding: '12px 18px', borderRadius: 12, cursor: 'pointer',
  border: `2px solid ${selected === item ? color.border : '#e5e3db'}`,
  background: selected === item ? color.bg : '#fff',
  color: selected === item ? color.text : '#888',
  fontWeight: 600, minWidth: 80, transition: 'all 0.15s',
  boxShadow: selected === item ? `0 0 0 3px ${color.border}22` : 'none',
});

export default ChargingPanel;
